import { createApi, type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { AxiosError } from 'axios'
import { formatDateTimeForApi } from '../utils'
import dayjs from 'dayjs'
import { IRole, ISecurityPrincipal } from '../../features/CommonSettings/RoleManagement/types'
import {
  AttemptVerifyDomainResponse,
  CreateVerifiedDomainRequest,
  CreateVerifiedDomainResponse,
  VerifiedDomainDto,
} from '../../features/CommonSettings/VerifiedDomains/types'
import {
  AddFederationDomainRequest,
  CreateFederationConsentUrlRequest,
  CreateFederationConsentUrlResponse,
  CreateFederationProviderRequest,
  FederationConsentState,
  FederationProviderListItem,
  FederationProviderType,
  LinkFederationDomainRequest,
} from '../../features/CommonSettings/FederationProviders/types'
import {
  ITrackingNumberParameterType,
  ITrackingNumberTemplate,
} from '../../features/CommonSettings/TrackingNumber/types'
import { IUserGroup } from '../../features/CommonSettings/UserManagement/UserGroups/types'
import {
  IInviteUserRequest,
  IInviteUserResponse,
  InviteUserApiError,
  IPagedUsers,
} from '../../features/CommonSettings/UserManagement/types'
import { ICurrency } from '../../features/Currency/types'
import {
  IIndustry,
  IRegisterCustomerRequest,
  RegisterCustomerApiError,
} from '../../features/CustomerSettings/Customer/types'
import { RoutesCount } from '../../features/Dashboard/type'
import { IDocumentCategory } from '../../features/Documents/types'
import { Note } from '../../features/Notes/types'
import {
  ContainerData,
  GetContainerDetailRequest,
} from '../../features/Shipments/components/TerminalInfo/types'
import { GetTemperatureDataRequest } from '../../features/TemperatureChart/types'
import { IBranding } from '../../features/TenantSettings/types'
import { IUnassginedRoute } from '../../features/Transport/components/Routes/types'
import { ITransportRouteLoad } from '../../features/Transport/types'
import { TransportJob } from '../../features/TransportJob/types'
import { ICustomer } from '../../features/types'
import { LOGIKSPARK_ORDERS_API_URL } from '../axios/const'
import snackbarUtil from '../snackbar/snackbarUtil'
import { IDropDownItem } from '../types'
import { fetchData } from './fetchData'
import { setLoading } from './loadingSlice'

const ExcludeLoadingUrls = ['refresh-inbound-container-status', 'temperature-series']

// 409s whose body code the calling component renders inline (e.g. the register-customer modal's
// duplicate-email field error, WI 514) must NOT also fire a global toast. The base query skips the toast
// for these — mirroring the existing 404 skip — while still surfacing every other error. The mutation's
// transformErrorResponse then turns the same body into a typed, component-handled result.
const InlineHandledErrorCodes = new Set(['DUPLICATE_EMAIL_IN_SCOPE', 'INVALID_GRANT_REQUEST'])

const apiAxiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<
    {
      url: string
      method: 'GET' | 'POST' | 'PUT' | 'DELETE'
      domain: 'Asset' | 'Shipment' | 'User' | 'Core' | 'Product'
      body?: any
      params?: any
    },
    unknown,
    unknown
  > =>
  async ({ url, method, domain, body, params }, api) => {
    const { dispatch } = api

    try {
      if (method !== 'GET' && !ExcludeLoadingUrls.some((u) => url.split('/').includes(u))) {
        dispatch(setLoading(true))
      }

      const queryParams = params
        ? `?${Object.entries(params)
            .map(
              ([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
            )
            .join('&')}`
        : ''

      const fullUrl = `${url}${queryParams}`

      return await fetchData(domain, method, fullUrl, body)
    } catch (axiosError) {
      const err = axiosError as AxiosError
      // Error bodies use the same ResponseDto envelope as success: { data: { code, message } }.
      const body = err.response?.data as any
      // Codes the caller renders inline (409 DUPLICATE_EMAIL_IN_SCOPE, 400 INVALID_GRANT_REQUEST) must NOT
      // also fire a global toast — the caller shows the message in-form.
      const handledInline =
        (err.response?.status === 409 || err.response?.status === 400) &&
        InlineHandledErrorCodes.has(body?.data?.code)
      // A 401 carrying an RFC-9470 claims challenge is not a real error — the axios response interceptor is
      // answering it with an MFA redirect (e.g. impersonation-session step-up). Suppress the toast (the page
      // is navigating away) and flag the error so callers can tell a step-up-in-flight from a genuine 401.
      // (Reading www-authenticate needs Access-Control-Expose-Headers on the API — the same header the
      // interceptor's challenge extractor relies on, so the two stay consistent: no expose → no redirect →
      // not flagged → toast shows, which is correct.)
      const wwwAuthenticate = err.response?.headers?.['www-authenticate']
      const isClaimsChallenge =
        err.response?.status === 401 &&
        typeof wwwAuthenticate === 'string' &&
        /claims=/.test(wwwAuthenticate)
      if (err.response?.status !== 404 && !handledInline && !isClaimsChallenge) {
        snackbarUtil.apiError(body)
      }

      return {
        error: {
          status: err.response?.status,
          data: body || err.message,
          // Surface Retry-After (integer seconds) so a 429 caller can drive an exact countdown
          // instead of a fixed fallback. Axios lowercases header keys.
          retryAfter: Number(err.response?.headers?.['retry-after']) || undefined,
          // True when a step-up MFA redirect is in flight (see above) — the impersonation start flow keeps
          // its pending record only in this case.
          isClaimsChallenge,
        },
      }
    } finally {
      if (method !== 'GET') {
        dispatch(setLoading(false))
      }
    }
  }

export const apiSlice = createApi({
  baseQuery: apiAxiosBaseQuery({
    baseUrl: LOGIKSPARK_ORDERS_API_URL,
  }),
  tagTypes: [
    'WorkforceType',
    'Shipments',
    'Carrier',
    'Assets',
    'Loads',
    'Journeys',
    'Journey',
    'AssetGroup',
    'TrackingNumberParameterTypes',
    'TrackingNumberTemplate',
    'AssetTypes',
    'AssetConfigurations',
    'InventoryUnits',
    'Brands',
    'Customers',
    'Branding',
    'Categories',
    'SubHsHeadings',
    'HsHeadings',
    'Tariffs',
    'Products',
    'ProductCategories',
    'Transport Jobs',
    'Shipment Notes',
    'Users',
    'UserGroups',
    'Workforce',
    'Temperature',
    'Roles',
    'Customer',
    'Unassigned Loads',
    'Currency',
    'Orders',
    'AssetHire',
    'AssetHires',
    'AssetHireSummaries',
    'Service',
    'HSHeadings',
    'SubHSHeadings',
    'ProductCategories',
    'LoadTemplate',
    'LoadContents',
    'ConsignmentLoads',
    'VerifiedDomains',
    'FederationProviders',
    'ImpersonationGrants',
  ],
  endpoints(build) {
    return {
      trackingNumberParamsTypes: build.query<ITrackingNumberParameterType[], { tenantId: number }>({
        query: ({ tenantId }) => {
          return {
            url: `/tenant/${tenantId}/trackingnumber/parameters`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
        providesTags: (result, error, { tenantId }) => [
          { type: 'TrackingNumberParameterTypes', tenantId },
        ],
      }),
      trackingNumberTemplate: build.query<
        ITrackingNumberTemplate,
        { tenantId: number; customerId?: number }
      >({
        query: ({ tenantId, customerId }) => {
          if (customerId) {
            return {
              url: `customer/${customerId}/trackingnumber/template`,
              method: 'GET',
              domain: 'Shipment',
            }
          }
          return {
            url: `/tenant/${tenantId}/trackingnumber/template`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
        providesTags: (result, error, { tenantId }) => [
          { type: 'TrackingNumberTemplate', tenantId },
        ],
      }),
      createTrackingNumberTemplate: build.mutation<
        any,
        { tenantId: number; body: ITrackingNumberTemplate }
      >({
        query: ({ tenantId, body }) => ({
          url: `/tenant/${tenantId}/trackingnumber/template`,
          method: 'POST',
          domain: 'Shipment',
          body: body,
        }),
      }),
      loadsByRouteId: build.query<ITransportRouteLoad[], { routeId: number; tenantId: number }>({
        query: ({ routeId, tenantId }) => {
          return {
            url: `/tenant/${tenantId}/route/${routeId}`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
        providesTags: (result, error, locationId) => [{ type: 'Loads', locationId }],
      }),
      getUnassignedRoutes: build.query<IUnassginedRoute[], { tenantId: number }>({
        query: ({ tenantId }) => {
          return {
            url: `/tenant/${tenantId}/route`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
        providesTags: (result, error, locationId) => [
          { type: 'Loads', locationId },
          { type: 'Loads', id: 'Unassigned Routes' },
        ],
      }),
      // Get customers by tenantId
      getCustomers: build.query<{ id: number; name: string; disabled?: boolean }[], number>({
        query: (tenantId: number) => ({
          url: `/tenant/${tenantId}/customer`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Customers', id: 'Customers' }],
      }),
      getCustomerById: build.query<ICustomer, { tenantId: number; customerId: number }>({
        query: ({ tenantId, customerId }) => ({
          url: `tenant/${tenantId}/customer/${customerId}`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Customers', id: 'Customers' }],
      }),
      setDefaultBranding: build.mutation<IBranding, number>({
        query: (tenantId: number) => ({
          url: `/tenant/${tenantId}/branding/reset`,
          method: 'POST',
          domain: 'User',
        }),
        invalidatesTags: () => [{ type: 'Branding', id: 'Branding' }],
      }),
      // Job //
      getTransportJobs: build.query<TransportJob[], number>({
        query: (tenantId) => ({
          url: `/tenant/${tenantId}/transport-job`,
          method: 'GET',
          domain: 'Shipment',
        }),
        providesTags: () => [{ type: 'Transport Jobs', id: 'Transport Jobs' }],
      }),
      // Job //
      // Notes //
      getNotesById: build.query<Note[], number>({
        query: (id: number) => ({
          url: `order/${id}/note`,
          method: 'GET',
          domain: 'Shipment',
        }),
        providesTags: () => [{ type: 'Shipment Notes', id: 'Shipment Notes' }],
      }),
      addNote: build.mutation<any, { body: Note }>({
        query: ({ body }) => ({
          url: 'order/note',
          method: 'POST',
          domain: 'Shipment',
          body: body,
        }),
      }),
      // Notes //
      // User Groups //
      getUserGroups: build.query<IUserGroup[], { tenantId: number }>({
        query: ({ tenantId }) => ({
          url: `tenant/${tenantId}/user-group`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'UserGroups', id: 'UserGroups' }],
      }),
      getUserGroupById: build.query<IUserGroup, { userGroupId: number }>({
        query: ({ userGroupId }) => ({
          url: `user-group/${userGroupId}`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'UserGroups', id: 'UserGroups' }],
      }),
      getRoles: build.query<IRole[], { tenantId?: number; customerId?: number }>({
        query: ({ tenantId, customerId }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/role`,
              method: 'GET',
              domain: 'User',
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/role`,
              method: 'GET',
              domain: 'User',
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        providesTags: () => [
          { type: 'Roles', id: 'Roles' },
          { type: 'UserGroups', id: 'UserGroups' },
        ],
      }),
      getRoleById: build.query<IRole, { roleId: number }>({
        query: ({ roleId }) => ({
          url: `role/${roleId}`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Roles', id: 'Roles' }],
      }),
      getSecurityPrincipals: build.query<ISecurityPrincipal[], void>({
        query: () => {
          return {
            url: '/security-principal',
            method: 'GET',
            domain: 'User',
          }
        },
      }),
      createRole: build.mutation<any, { tenantId?: number; customerId?: number; body: IRole }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/role`,
              method: 'POST',
              domain: 'User',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/role`,
              method: 'POST',
              domain: 'User',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: () => [{ type: 'Roles', id: 'Roles' }],
      }),
      updateRole: build.mutation<any, { tenantId?: number; customerId?: number; body: IRole }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/role`,
              method: 'PUT',
              domain: 'User',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/role`,
              method: 'PUT',
              domain: 'User',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: () => [{ type: 'Roles', id: 'Roles' }],
      }),

      getVerifiedDomains: build.query<
        VerifiedDomainDto[],
        { tenantId: number; customerId?: number }
      >({
        query: ({ tenantId, customerId }) =>
          customerId
            ? {
                url: `/tenant/${tenantId}/customer/${customerId}/verified-domain`,
                method: 'GET',
                domain: 'User',
              }
            : { url: `/tenant/${tenantId}/verified-domain`, method: 'GET', domain: 'User' },
        providesTags: () => [{ type: 'VerifiedDomains', id: 'VerifiedDomains' }],
      }),
      createVerifiedDomain: build.mutation<
        CreateVerifiedDomainResponse,
        { tenantId: number; body: CreateVerifiedDomainRequest }
      >({
        query: ({ tenantId, body }) => ({
          url: `/tenant/${tenantId}/verified-domain`,
          method: 'POST',
          domain: 'User',
          body,
        }),
        invalidatesTags: () => [{ type: 'VerifiedDomains', id: 'VerifiedDomains' }],
      }),
      verifyDomain: build.mutation<AttemptVerifyDomainResponse, { tenantId: number; id: number }>({
        query: ({ tenantId, id }) => ({
          url: `/tenant/${tenantId}/verified-domain/${id}/verify`,
          method: 'POST',
          domain: 'User',
        }),
        invalidatesTags: () => [{ type: 'VerifiedDomains', id: 'VerifiedDomains' }],
      }),
      deleteVerifiedDomain: build.mutation<number, { tenantId: number; id: number }>({
        query: ({ tenantId, id }) => ({
          url: `/tenant/${tenantId}/verified-domain/${id}`,
          method: 'DELETE',
          domain: 'User',
        }),
        invalidatesTags: () => [{ type: 'VerifiedDomains', id: 'VerifiedDomains' }],
      }),

      // E12 F12-5 (WI 633-637) — federation providers. All AO-only + tenant-scoped server-side from the
      // request context (no tenantId in the URL); the base query attaches the bearer + persona headers.
      getFederationProviders: build.query<FederationProviderListItem[], void>({
        query: () => ({ url: 'federation/providers', method: 'GET', domain: 'User' }),
        providesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      getFederationProviderTypes: build.query<FederationProviderType[], void>({
        query: () => ({ url: 'federation/types', method: 'GET', domain: 'User' }),
      }),
      createFederationProvider: build.mutation<number, CreateFederationProviderRequest>({
        query: (body) => ({ url: 'federation/providers', method: 'POST', domain: 'User', body }),
        invalidatesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      deleteFederationProvider: build.mutation<number, { id: number }>({
        query: ({ id }) => ({ url: `federation/providers/${id}`, method: 'DELETE', domain: 'User' }),
        invalidatesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      addFederationDomain: build.mutation<number, { providerId: number; body: AddFederationDomainRequest }>({
        query: ({ providerId, body }) => ({
          url: `federation/providers/${providerId}/domains`,
          method: 'POST',
          domain: 'User',
          body,
        }),
        invalidatesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      deleteFederationDomain: build.mutation<number, { providerId: number; domainId: number }>({
        query: ({ providerId, domainId }) => ({
          url: `federation/providers/${providerId}/domains/${domainId}`,
          method: 'DELETE',
          domain: 'User',
        }),
        invalidatesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      linkFederationDomain: build.mutation<
        number,
        { providerId: number; domainId: number; body: LinkFederationDomainRequest }
      >({
        query: ({ providerId, domainId, body }) => ({
          url: `federation/providers/${providerId}/domains/${domainId}/verified-domain`,
          method: 'PUT',
          domain: 'User',
          body,
        }),
        invalidatesTags: () => [{ type: 'FederationProviders', id: 'FederationProviders' }],
      }),
      // E20 F20-7 (WI 794) — the Microsoft admin-consent round trip. No invalidatesTags on the mutation
      // (it returns a computed value without changing the provider list) and
      // no FederationProviders tag on the poll query (a provider mutation must not invalidate it mid-poll).
      createFederationConsentUrl: build.mutation<
        CreateFederationConsentUrlResponse,
        CreateFederationConsentUrlRequest
      >({
        query: (body) => ({ url: 'v1/federation/consent-url', method: 'POST', domain: 'User', body }),
      }),
      getFederationConsentState: build.query<FederationConsentState, string>({
        query: (key) => ({ url: `v1/federation/consent-state/${key}`, method: 'GET', domain: 'User' }),
      }),

      createUserGroup: build.mutation<any, { tenantId?: number; customerId?: number; body: any }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/user-group`,
              method: 'POST',
              domain: 'User',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/user-group`,
              method: 'POST',
              domain: 'User',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: () => [{ type: 'UserGroups', id: 'UserGroups' }],
      }),
      updateUserGroup: build.mutation<any, { tenantId?: number; customerId?: number; body: any }>({
        query: ({ tenantId, customerId, body }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/user-group`,
              method: 'PUT',
              domain: 'User',
              body,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/user-group`,
              method: 'PUT',
              domain: 'User',
              body,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        invalidatesTags: () => [{ type: 'UserGroups', id: 'UserGroups' }],
      }),
      // User Groups //
      // Users //
      createUser: build.mutation<any, { tenantId: number; body: any }>({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/user`,
          method: 'POST',
          domain: 'User',
          body: body,
        }),
        invalidatesTags: [
          { type: 'Workforce', id: 'Workforce' },
          { type: 'Users', id: 'Users' },
        ],
      }),
      getUser: build.query<any, { userId: number }>({
        query: ({ userId }) => ({
          url: `user/${userId}`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      getUserTypes: build.query<IDropDownItem[], { tenantId?: number; customerId?: number }>({
        query: ({ tenantId, customerId }) => {
          if (customerId) {
            return {
              url: `/customer/${customerId}/workforce-type`,
              method: 'GET',
              domain: 'User',
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/workforce-type`,
              method: 'GET',
              domain: 'User',
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        providesTags: () => [{ type: 'Workforce', id: 'Workforce' }],
      }),
      updateUser: build.mutation<any, { tenantId: number; body: any }>({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/user`,
          method: 'PUT',
          domain: 'User',
          body: body,
        }),
        invalidatesTags: [
          { type: 'Workforce', id: 'Workforce' },
          { type: 'Users', id: 'Users' },
        ],
      }),
      deleteUser: build.mutation<any, { tenantId: number; userId: number }>({
        query: ({ tenantId, userId }) => ({
          url: `tenant/${tenantId}/user/${userId}`,
          method: 'DELETE',
          domain: 'User',
        }),
        invalidatesTags: [
          { type: 'Workforce', id: 'Workforce' },
          { type: 'Users', id: 'Users' },
        ],
      }),
      createWorkforce: build.mutation<any, { tenantId: number; body: any }>({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/workforce`,
          method: 'POST',
          domain: 'User',
          body: body,
        }),
        invalidatesTags: [
          { type: 'Workforce', id: 'Workforce' },
          { type: 'Users', id: 'Users' },
        ],
      }),
      // Users //
      getWorkforce: build.query<any, { userId: number }>({
        query: ({ userId }) => ({
          url: `workforce/${userId}`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      getUsers: build.query<
        any,
        { tenantId?: number; customerId?: number; userGroupName?: string }
      >({
        query: ({ tenantId, customerId, userGroupName }) => {
          const params = userGroupName ? { userGroupName } : undefined
          if (customerId) {
            return {
              url: `/customer/${customerId}/user`,
              method: 'GET',
              domain: 'User',
              params,
            }
          } else if (tenantId) {
            return {
              url: `/tenant/${tenantId}/user`,
              method: 'GET',
              domain: 'User',
              params,
            }
          } else {
            throw new Error('Either tenantId or customerId must be provided')
          }
        },
        providesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      // E10 F10-7 — one keyset-paged page of the tenant's users with a computed lifecycle Status, merged
      // into the User Management grid (by id) to add the Status column + the invitation row actions. Same
      // scope as getUsers' tenant path (tenant-direct users), so the merge aligns row-for-row.
      getUsersPaged: build.query<
        IPagedUsers,
        { tenantId: number; pageSize?: number; pageToken?: number }
      >({
        query: ({ tenantId, pageSize, pageToken }) => {
          const params: Record<string, number> = {}
          if (pageSize) params.pageSize = pageSize
          if (pageToken) params.pageToken = pageToken
          return {
            url: `/tenant/${tenantId}/user/paged`,
            method: 'GET',
            domain: 'User',
            params: Object.keys(params).length ? params : undefined,
          }
        },
        providesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      // E10 F10-2 — invite a user into the caller's workspace. Tenant comes from the caller's persona
      // server-side, so it is not in the body. Invalidates Users so the grid refetches the new Pending row.
      // transformErrorResponse turns the 409 DUPLICATE_EMAIL_IN_SCOPE envelope into a typed error the
      // modal renders inline (the base query already skips the toast for that code).
      inviteUser: build.mutation<
        IInviteUserResponse,
        { body: IInviteUserRequest }
      >({
        query: ({ body }) => ({
          url: '/v1/users/invitations',
          method: 'POST',
          domain: 'User',
          body,
        }),
        invalidatesTags: () => [{ type: 'Users', id: 'Users' }],
        transformErrorResponse: (resp): InviteUserApiError => {
          const r = resp as { status?: number; data?: any }
          const body = r?.data?.data ?? r?.data
          const code: string | undefined = body?.code
          const message: string | undefined = body?.message
          if (r.status === 409 && code === 'DUPLICATE_EMAIL_IN_SCOPE') {
            return { kind: 'duplicate-email', message: message ?? '' }
          }
          return { kind: 'unknown', status: r.status, message: message ?? '' }
        },
      }),
      // E10 F10-4 — re-issue (resend) a user's invitation: revokes the old Pending one and mints a fresh
      // tokenised email. Keyed off the user, so an 'Expired' row (no live invitation) can be re-invited.
      reissueInvitation: build.mutation<void, { userId: number }>({
        query: ({ userId }) => ({
          url: `/v1/users/invitations/${userId}/resend`,
          method: 'POST',
          domain: 'User',
        }),
        invalidatesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      // E10 F10-4 — revoke a Pending invitation by its id. Optional deleteUserToo soft-deletes the freshly
      // created (never-activated) user along with the invitation.
      revokeInvitation: build.mutation<
        void,
        { invitationId: number; deleteUserToo?: boolean }
      >({
        query: ({ invitationId, deleteUserToo }) => ({
          url: `/v1/invitations/${invitationId}/revoke`,
          method: 'POST',
          domain: 'User',
          body: { deleteUserToo: deleteUserToo ?? false },
        }),
        invalidatesTags: () => [{ type: 'Users', id: 'Users' }],
      }),
      getWorkforces: build.query<any, { tenantId: number }>({
        query: ({ tenantId }) => ({
          url: `tenant/${tenantId}/workforce`,
          method: 'GET',
          domain: 'User',
        }),
        providesTags: () => [{ type: 'Workforce', id: 'Workforce' }],
      }),
      // Reports //
      getShipmentsReport: build.query<
        any,
        { tenantId: number; startDate: string; endDate: string }
      >({
        query: ({ tenantId, startDate, endDate }) => {
          const formattedStartDate = formatDateTimeForApi(startDate)
          const formattedEndDate = formatDateTimeForApi(endDate)
          const queryParams = new URLSearchParams({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }).toString()

          return {
            url: `tenant/${tenantId}/shipment/report?${queryParams}`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
      }),
      getShipmentsReportByPickupCountry: build.query<
        any,
        { tenantId: number; startDate: string; endDate: string }
      >({
        query: ({ tenantId, startDate, endDate }) => {
          const formattedStartDate = formatDateTimeForApi(startDate)
          const formattedEndDate = formatDateTimeForApi(endDate)
          const queryParams = new URLSearchParams({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }).toString()

          return {
            url: `tenant/${tenantId}/shipments/grouped-pickup-country?${queryParams}`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
      }),
      getDriverOperations: build.query<
        any,
        { tenantId: number; startDate: string; endDate: string }
      >({
        query: ({ tenantId, startDate, endDate }) => {
          const formattedStartDate = formatDateTimeForApi(startDate)
          const formattedEndDate = formatDateTimeForApi(endDate)
          const queryParams = new URLSearchParams({
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }).toString()

          return {
            url: `tenant/${tenantId}/drivers/operations?${queryParams}`,
            method: 'GET',
            domain: 'User',
          }
        },
      }),

      getRoutesCount: build.query<RoutesCount, { tenantId: number }>({
        query: ({ tenantId }) => {
          return {
            url: `tenant/${tenantId}/Report/routes-count`,
            method: 'GET',
            domain: 'Shipment',
          }
        },
      }),
      getTemperatureData: build.mutation<
        any,
        { tenantId: number; shipmentId: number; body: GetTemperatureDataRequest }
      >({
        query: ({ tenantId, shipmentId, body }) => ({
          url: `/tenant/${tenantId}/order/${shipmentId}/shipment/temperature-series`,
          method: 'POST',
          domain: 'Shipment',
          body: body,
        }),
        invalidatesTags: () => [{ type: 'Temperature', id: 'LoadTemperatureData' }],
      }),
      // Industry //
      getIndustries: build.query<IIndustry[], void>({
        query: () => ({
          url: 'industry',
          method: 'GET',
          domain: 'User',
        }),
      }),
      // Industry //
      // Customer //
      postCustomer: build.mutation<any, { tenantId: number; body: ICustomer }>({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/customer`,
          method: 'POST',
          domain: 'User',
          body: body,
        }),
        invalidatesTags: () => [{ type: 'Customers', id: 'Customers' }],
      }),
      putCustomerCustomer: build.mutation<any, { tenantId: number; body: ICustomer }>({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/customer`,
          method: 'PUT',
          domain: 'User',
          body: body,
        }),
        invalidatesTags: () => [{ type: 'Customers', id: 'Customers' }],
      }),
      // E5 (WI 511/514) — create a Customer + its Account Owner under an existing Tenant. Invalidates the
      // Customers tag so the list refetches. transformErrorResponse turns the 409 envelope into a typed
      // RegisterCustomerApiError; the modal renders DUPLICATE_EMAIL_IN_SCOPE inline (no toast — the base
      // query skips it) and toasts everything else.
      registerCustomer: build.mutation<
        { customerId: number; userId: number },
        { tenantId: number; body: IRegisterCustomerRequest }
      >({
        query: ({ tenantId, body }) => ({
          url: `tenant/${tenantId}/customer/register`,
          method: 'POST',
          domain: 'User',
          body,
        }),
        invalidatesTags: () => [{ type: 'Customers', id: 'Customers' }],
        transformErrorResponse: (resp): RegisterCustomerApiError => {
          // The base query returns { status, data } where data is the ResponseDto envelope
          // { data: { code, message } } (falling back to a bare string on a network error).
          const r = resp as { status?: number; data?: any }
          const body = r?.data?.data ?? r?.data
          const code: string | undefined = body?.code
          const message: string | undefined = body?.message
          if (r.status === 409 && code === 'DUPLICATE_EMAIL_IN_SCOPE') {
            return { kind: 'duplicate-email', message: message ?? '' }
          }
          if (r.status === 409) {
            return { kind: 'other-conflict', code: code ?? 'CONFLICT', message: message ?? 'Conflict' }
          }
          return { kind: 'unknown', status: r.status, message: message ?? '' }
        },
      }),
      getContainerDetails: build.mutation<ContainerData, { body: GetContainerDetailRequest }>({
        query: ({ body }) => ({
          url: 'refresh-inbound-container-status',
          method: 'POST',
          domain: 'Shipment',
          body: body,
        }),
      }),
      getDocumentCategory: build.query<IDocumentCategory[], { id: number }>({
        query: ({ id }) => ({
          url: `document-category/${id}`,
          method: 'GET',
          domain: 'Core',
        }),
      }),
      getSystemCurrency: build.query<ICurrency[], void>({
        query: () => ({
          url: 'currency',
          method: 'GET',
          domain: 'Core',
        }),
      }),
      // Core //
    }
  },
})

export const {
  useLoadsByRouteIdQuery,
  useGetUnassignedRoutesQuery,
  useTrackingNumberParamsTypesQuery,
  useTrackingNumberTemplateQuery,
  useCreateTrackingNumberTemplateMutation,
  // Users //
  useGetCustomersQuery,
  useSetDefaultBrandingMutation,
  // Transport Jobs //
  useGetTransportJobsQuery,
  // Transport Jobs //
  // Notes //
  useGetNotesByIdQuery,
  useAddNoteMutation,
  // Notes //
  // Workforce //
  useCreateUserMutation,
  useCreateWorkforceMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetWorkforceQuery,
  useGetWorkforcesQuery,
  useGetUserTypesQuery,
  // Workforce //
  // Report //
  useGetShipmentsReportQuery,
  useGetShipmentsReportByPickupCountryQuery,
  useGetDriverOperationsQuery,
  useGetRoutesCountQuery,
  // Report //
  // Temperature Data //
  useGetTemperatureDataMutation,
  // Temperature Data //
  // Industry //
  useGetIndustriesQuery,
  // Industry //
  // Customer //
  usePostCustomerMutation,
  usePutCustomerCustomerMutation,
  useRegisterCustomerMutation,
  useGetCustomerByIdQuery,
  // Customer //
  useGetContainerDetailsMutation,
  // Customer //
  // Users //
  useGetUsersQuery,
  useGetUsersPagedQuery,
  useInviteUserMutation,
  useReissueInvitationMutation,
  useRevokeInvitationMutation,
  useDeleteUserMutation,
  // Users //
  // User Groups //
  useGetUserGroupsQuery,
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
  useGetUserGroupByIdQuery,
  // User Groups //
  // Roles //
  useGetRolesQuery,
  useGetSecurityPrincipalsQuery,
  useGetRoleByIdQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useGetVerifiedDomainsQuery,
  useCreateVerifiedDomainMutation,
  useVerifyDomainMutation,
  useDeleteVerifiedDomainMutation,
  // Federation //
  useGetFederationProvidersQuery,
  useGetFederationProviderTypesQuery,
  useCreateFederationProviderMutation,
  useDeleteFederationProviderMutation,
  useAddFederationDomainMutation,
  useDeleteFederationDomainMutation,
  useLinkFederationDomainMutation,
  useCreateFederationConsentUrlMutation,
  useGetFederationConsentStateQuery,
  // Federation //
  // Roles //
  useGetSystemCurrencyQuery,
  endpoints: apiEndpoints,
} = apiSlice
